import {
  DragEvent, useCallback, useRef, useState,
} from 'react';

const carriesFiles = (event: DragEvent<HTMLElement>) => (
  Array.from(event.dataTransfer?.types ?? []).includes('Files')
);

/**
 * Área que recebe arquivos arrastados do computador. O contador de `dragenter`/`dragleave`
 * evita o "pisca" ao passar sobre elementos filhos (cada filho dispara o par de eventos).
 */
export default function useFileDrop({
  enabled,
  onFiles,
}: {
  enabled: boolean;
  onFiles: (files: File[]) => void;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const depth = useRef(0);

  const onDragEnter = useCallback((event: DragEvent<HTMLElement>) => {
    if (!enabled || !carriesFiles(event)) return;
    event.preventDefault();
    depth.current += 1;
    setIsDragging(true);
  }, [enabled]);

  const onDragOver = useCallback((event: DragEvent<HTMLElement>) => {
    if (!enabled || !carriesFiles(event)) return;
    event.preventDefault();
    // eslint-disable-next-line no-param-reassign
    event.dataTransfer.dropEffect = 'copy';
  }, [enabled]);

  const onDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    if (!enabled || !carriesFiles(event)) return;
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setIsDragging(false);
  }, [enabled]);

  const onDrop = useCallback((event: DragEvent<HTMLElement>) => {
    if (!enabled || !carriesFiles(event)) return;
    event.preventDefault();
    depth.current = 0;
    setIsDragging(false);
    const files = Array.from(event.dataTransfer.files ?? []);
    if (files.length) onFiles(files);
  }, [enabled, onFiles]);

  return {
    isDragging: enabled && isDragging,
    handlers: {
      onDragEnter, onDragOver, onDragLeave, onDrop,
    },
  };
}
